import { Input } from '@/view/components/ui/input'
import { useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

interface PinInputProps {
  pin: string[]
  setPin: React.Dispatch<React.SetStateAction<string[]>>
}

export function PinInput({ pin, setPin }: PinInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [isVisible, setIsVisible] = useState(false)

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    if (value.length > 1) return

    const newPin = [...pin]
    newPin[index] = value
    setPin(newPin)

    if (value !== '' && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && index > 0 && pin[index] === '') {
      const newPin = [...pin]
      newPin[index - 1] = ''
      setPin(newPin)
      inputRefs.current[index - 1]?.focus()
    }
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
  }

  return (
    <div className="relative flex w-full items-center justify-start gap-8">
      {pin.map((digit, index) => (
        <div key={index} className="relative">
          <Input
            type={isVisible ? 'text' : 'password'}
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            className="h-12 w-12 rounded-md border-2 border-gray-300 bg-transparent text-center caret-transparent selection:bg-transparent focus:border-primary focus:ring-2 focus:ring-primary/20"
            value={digit}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            ref={(el) => (inputRefs.current[index] = el)}
          />
          {!isVisible && (
            <div className="pointer-events-none absolute inset-0 top-2 flex items-center justify-center text-2xl font-bold">
              {digit ? '*' : ''}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={toggleVisibility}
        className="absolute right-0 top-1/2 -translate-y-1/2 transform p-2"
        aria-label={isVisible ? 'Hide PIN' : 'Show PIN'}
      >
        {isVisible ? (
          <EyeOff className="h-5 w-5 text-gray-500" />
        ) : (
          <Eye className="h-5 w-5 text-gray-500" />
        )}
      </button>
    </div>
  )
}
